import React, { useEffect, useState } from "react";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import MultiSelectDropdown from "../../../ui/MultiSelectDropdown";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { toast } from "react-toastify";
import { assignCouponToUsers } from "../../../../redux/features/CouponSlice";
import { fetchUsersAsync } from "../../../../redux/features/adminSlice";
import { mapDataToLabelValue } from "../../../../helpers";

interface AssignCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedCoupon: any;
}

const AssignCouponModal: React.FC<AssignCouponModalProps> = ({
  open,
  onClose,
  onSave,
  selectedCoupon,
}) => {
  const dispatch = useAppDispatch();
  const { vendors, buyers } = useAppSelector((state: RootState) => state.admin);
  const { assigningCoupon } = useAppSelector(
    (state: RootState) => state.coupons
  );

  const [formData, setFormData] = useState({
    userType: "",
    users: [] as { label: string; value: string }[],
  });

  const [search, setSearch] = useState("");

  const [errors, setErrors] = useState({
    userType: "",
    users: "",
  });

  const validateForm = () => {
    const newErrors = { userType: "", users: "" };
    let valid = true;

    if (!formData.userType) {
      newErrors.userType = "User type is required";
      valid = false;
    }
    if (!formData.users.length) {
      newErrors.users = "At least one user must be selected";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSelectChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "userType") {
      setFormData((prev) => ({ ...prev, users: [] }));
    }
  };

  // Fetch users from Redux based on search input
  useEffect(() => {
    if (formData.userType) {
      dispatch(
        fetchUsersAsync({
          page: 1,
          limit: 50,
          filter: search,
          userType: formData.userType,
        })
      );
    }
  }, [search, formData.userType, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const users = formData.users.map((u) => u.value);
    const result = await dispatch(
      assignCouponToUsers({ couponId: selectedCoupon.id, users })
    );

    if (result?.meta?.requestStatus === "fulfilled") {
      toast.success("Coupon assigned successfully");
      onClose();
      onSave();
    } else {
      toast.error("Failed to assign coupon");
    }
  };

  if (!open) return null;

  const userOptions = mapDataToLabelValue(
    formData.userType === "BUYER" ? buyers?.data?.data : vendors?.data?.data,
    "fullName",
    "id"
  );

  console.log("selectedCoupon: ", selectedCoupon);

  return (
    <CustomModalContainer
      title="Assign Coupon"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            type="button"
            onClick={onClose}
            label="Cancel"
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            type="submit"
            label="Assign Coupon"
            onClick={handleSubmit}
            isLoading={assigningCoupon}
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
          />
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="p-6">
        {/* Coupon Code (Read-only) */}
        <ModalInput
          label="Coupon Code"
          name="couponCode"
          value={selectedCoupon?.couponCode || ""}
          onChange={() => {}}
          readonly
          error=""
          required={false}
        />
        {/* Coupon type (Read-only) */}
        <ModalInput
          label="Coupon Code"
          name="couponCode"
          value={selectedCoupon?.couponType || ""}
          onChange={() => {}}
          readonly
          error=""
          required={false}
        />

        {/* User Type Dropdown */}
        <Dropdown
          label="User Type"
          name="userType"
          options={[
            { label: "Buyer", value: "BUYER" },
            { label: "Vendor", value: "VENDOR" },
          ]}
          placeholder="Select user type"
          value={formData.userType}
          onSelect={(value) => handleSelectChange("userType", value)}
          error={errors.userType}
          required
        />

        {/* Multi User Dropdown (Search-based) */}
        <MultiSelectDropdown
          label="Users"
          placeholder="Search and select users"
          selectedValues={formData.users}
          onSelect={(values) => handleSelectChange("users", values)}
          options={userOptions}
          onSearchChange={setSearch}
          error={errors.users}
          required
        />
      </form>
    </CustomModalContainer>
  );
};

export default AssignCouponModal;
